<script setup lang="ts">
import { ActionFields } from '~/components/cms/Action/actionHelpers'
import { MediaProps } from '~/components/cms/Media/Media.vue'
import { getForeground, BG } from '~/components/cms/RichTextPanel/richTextHelpers'

type HeroPanelProps = {
  tagline?: string
  titleMD?: string
  text?: string
  action?: ActionFields
  secondaryCallToAction?: ActionFields[]
  media: MediaProps
  variant: 'fullWidthHero' | 'fullWidthImage' | 'unknown'
  supportingData?: {
    home: {
      variant?: {
        children?: {
          verticalAlignment?: {
            value?: '' | 'centered'
          }
          showGradient?: {
            value?: boolean
          }
          backgroundColour?: {
            value?: BG
            children?: {
              fontColour?: {
                value?: string
              }
            }
          }
        }
      }
    }
  }
}

const props = withDefaults(defineProps<HeroPanelProps>(), {
  variant: 'fullWidthHero',
})

const variantClasses = {
  fullWidthHero: 'hero-panel--hero',
  fullWidthImage: 'hero-panel--image',
} as const

const variant = props.variant === 'unknown' ? 'fullWidthHero' : props.variant
const classes = variantClasses[variant]

const hasMedia = !!props.media?.sys
const showGradient = props.supportingData?.home?.variant?.children?.showGradient?.value !== false
const bgColour = props.supportingData?.home?.variant?.children?.backgroundColour?.value
const textColour = bgColour
  ? props.supportingData?.home?.variant?.children?.backgroundColour?.children?.fontColour?.value ||
    getForeground(bgColour)
  : undefined
/* Only apply invert theme when no explicit background colour is set */
const theme = variant === 'fullWidthHero' && !bgColour ? 'invert' : ''
/* Centre content when no media is present; otherwise use CMS vertical alignment config */
const vAlign = !hasMedia
  ? 'center'
  : props.supportingData?.home?.variant?.children?.verticalAlignment?.value === 'centered'
    ? 'center'
    : 'flex-end'
</script>

<template>
  <section
    class="hero-panel"
    data-testid="hero-panel"
    :class="classes"
    :style="{ backgroundColor: bgColour, color: textColour }"
  >
    <div class="hero-panel__media">
      <Media v-bind="media" loading="eager" fetchpriority="high" />
    </div>
    <div
      v-if="variant === 'fullWidthHero' && showGradient && (tagline || titleMD || text)"
      class="hero-panel__overlay"
    />

    <div class="hero-panel__content">
      <div class="container" :data-theme="theme" :style="{ 'justify-content': vAlign }">
        <p class="hero-panel__tagline" v-if="tagline">{{ tagline }}</p>
        <Markdown class="hero-panel__title" :content="titleMD" tag="h1" />
        <Markdown class="hero-panel__text" :content="text" />
        <div class="hero-panel__actions">
          <Action v-if="action?.fields" data-testid="hero-panel__action" v-bind="action" />
          <Action
            v-if="secondaryCallToAction?.[0]?.fields"
            data-testid="hero-panel__action"
            v-bind="secondaryCallToAction[0]"
          />
        </div>
      </div>
    </div>
  </section>
</template>

<style lang="postcss">
@import '~/assets/css/placeholders.css';

.hero-panel {
  --height: min(768px, 80vh);
  height: var(--height);
  margin-bottom: var(--component-space);
  min-height: 25rem;
  display: grid;
  grid-template-areas: 'fullspan';

  .hero-panel__media,
  .hero-panel__content,
  .hero-panel__overlay {
    grid-area: fullspan;
  }

  .hero-panel__overlay {
    height: 50%;
    align-self: end;
    background-image: linear-gradient(180deg, transparent, rgba(0, 0, 0, 1));
  }

  .hero-panel__media {
    max-height: var(--height);
    overflow: hidden;

    img {
      height: var(--height);
      width: 100%; /* required for centering fit:cover */
      min-width: 100vw;
      object-fit: cover;
    }

    .video {
      height: var(--height);
    }

    video {
      height: var(--height);
      width: 100vw;
      min-width: 20rem;
      object-fit: cover;
    }

    @media (--tablet) {
      max-height: 958px; /* minimize layout shift */
    }
  }

  .hero-panel__content {
    position: relative;
    z-index: 1;
    text-align: center;
    pointer-events: none;

    a,
    button {
      pointer-events: auto;
    }

    p:last-child {
      margin-bottom: 0;
    }
  }

  .hero-panel__tagline {
    letter-spacing: var(--letter-spacing-2);
    text-transform: uppercase;
    font-weight: bold;
  }

  .hero-panel__title {
    margin-bottom: 0;

    > p {
      margin-bottom: 0; /* p that may come from Markdown formatting */
    }
  }

  .hero-panel__text {
    @extend %body;
    margin-bottom: 0;
    max-width: var(--text-max-width); /* 416px */
  }

  .hero-panel__actions {
    width: 100%;
    display: flex;
    gap: var(--space-50);
    align-items: center;
    flex-direction: column;

    a,
    button {
      width: 100%;
      max-width: var(--text-max-width);
    }

    @media (--desktop) {
      flex-direction: row;
      justify-content: center;

      a,
      button {
        width: fit-content;
      }
    }
  }
}

/* ------- Variant: Full Width Hero Panel  ---------- */

.hero-panel--hero .hero-panel__title {
  @extend %heading-0;
}

.hero-panel--hero {
  .container {
    max-width: 40rem; /* 640px */
    display: flex;
    align-items: center;
    flex-direction: column;
    gap: var(--space-70);
    justify-content: flex-end;
    padding-inline: var(--space-70);
    padding-block: var(--space-100);
  }

  @media (--tablet) {
    --height: min(958px, 70vh);

    .hero-panel__title {
      margin-bottom: var(--space-70); /* 64px required = grid gap + margin */
    }
  }

  @media (--desktop) {
    .container {
      padding-bottom: var(--space-120);
    }
  }
}

/* ------- Variant: Full Width Image  ---------- */

.hero-panel--image {
  --height: 17.5rem;
  min-height: auto;
  @media (--desktop) {
    --height: 25rem;
  }

  .container {
    max-width: 40rem; /* 640px */
    display: flex;
    align-items: center;
    flex-direction: column;
    gap: var(--space-50);
    justify-content: flex-end;
    padding-bottom: var(--space-90);
    margin-block: 0;
  }
}
</style>
